import { useState, memo } from 'react';
import { Download, FileText, Info } from 'lucide-react';

// ChartWrapper - wraps charts with a title, info tooltip, data download button, and description placeholder
export const ChartWrapper = memo(function ChartWrapper({ title, info, chartData, sourceResults, exportFilename, description, onDescriptionChange, hideDescription, children }) {
  const [exporting, setExporting] = useState(false);

  const handleDownload = async () => {
    if (!chartData || chartData.length === 0) return;
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary (aggregated chart data)
      const summarySheet = XLSX.utils.json_to_sheet(chartData);
      XLSX.utils.book_append_sheet(wb, summarySheet, sourceResults?.length ? 'Summary' : 'Data');

      // Sheet 2: Results detail (when underlying result objects are provided)
      if (sourceResults && sourceResults.length > 0) {
        const { mapChartExportRows } = await import('../../../utils/dataProcessor/helpers');
        const resultsSheet = XLSX.utils.json_to_sheet(mapChartExportRows(sourceResults));
        XLSX.utils.book_append_sheet(wb, resultsSheet, 'Results');
      }

      XLSX.writeFile(wb, `${exportFilename || 'chart-data'}.xlsx`);
    } catch (err) {
      console.error('Failed to export chart data:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-[var(--surface-primary)] rounded-xl border border-[var(--border-primary)] p-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-[var(--text-primary)]">{title}</h4>
          {info && (
            <div className="relative group/info">
              <Info className="w-4 h-4 text-[var(--text-tertiary)] cursor-help hover:text-cgiar-green transition-colors" />
              <div className="invisible opacity-0 group-hover/info:visible group-hover/info:opacity-100 transition-all duration-150 absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 z-50 w-64 sm:w-72 p-3 bg-gray-900 text-white text-xs leading-relaxed rounded-lg shadow-lg pointer-events-none">
                <div className="absolute -top-1 left-4 sm:left-1/2 sm:-translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                {info}
              </div>
            </div>
          )}
        </div>
        {chartData && chartData.length > 0 && (
          <button
            onClick={handleDownload}
            disabled={exporting}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--text-tertiary)] hover:text-cgiar-green hover:bg-cgiar-green-tint rounded transition-colors disabled:opacity-50"
            title="Download data as .xlsx"
          >
            <Download className="w-3.5 h-3.5" />
            {exporting ? 'Exporting...' : 'Download data'}
          </button>
        )}
      </div>
      {children}
      {/* Chart description placeholder */}
      {!hideDescription && <div className="mt-4 pt-3 border-t border-[var(--border-secondary)]">
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-[var(--text-tertiary)] mt-0.5 flex-shrink-0" />
          {onDescriptionChange ? (
            <textarea
              value={description || ''}
              onChange={e => onDescriptionChange(e.target.value)}
              placeholder={`Describe what this chart shows — key trends, patterns, and insights from the "${title}" visualization...`}
              rows={2}
              className="flex-1 text-sm text-[var(--text-secondary)] bg-[var(--surface-secondary)] border border-[var(--border-primary)] rounded-lg px-3 py-2 placeholder-[var(--text-tertiary)] focus:ring-2 focus:ring-cgiar-green focus:border-transparent resize-none"
            />
          ) : (
            <p className={`text-sm italic ${description ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}`}>
              {description || `This chart shows ${title.toLowerCase()}.`}
            </p>
          )}
        </div>
      </div>}
    </div>
  );
});
